import { getHttpClient } from '@vnetwork/margarita-core/http';
import {
  defineWorkspaceService,
  normalizeId,
  type OrgWorkspace,
  type WorkspaceClient,
} from '@vnetwork/margarita-shell/iam';
import { K8S_V2_BASE } from '../paths';
import { K8S_WORKSPACE_KEY, readK8sWorkspace } from './workspace-answer';

const SHARED_KEY = ['k8s2', 'workspaces-shared'] as const;

/** The service type the registry and `resolveOrganizationWorkspace` know Kubernetes by. */
const KUBERNETES_WORKSPACE_TYPE = 'kubernetes';

interface HomeWorkspace {
  name?: string;
  organization_id: string;
}

/** One item of GET /api/v2/workspaces/shared (kubernetes internal/api/iam_shared.go sharedWorkspaceItem). */
interface SharedItem {
  organization_id: string;
  name?: string;
  org_wide?: boolean;
  project_ids?: string[];
  cluster_ids?: string[];
}

/** Both answers name their resource under its own key and carry no envelope (`_shared.ts`). */
const field = <T>(body: unknown, key: string): T | undefined =>
  body && typeof body === 'object' ? ((body as Record<string, unknown>)[key] as T | undefined) : undefined;

function sharedEntries(items: readonly SharedItem[]): OrgWorkspace[] {
  return items.map((w) => {
    const projectIds = w.project_ids ?? [];
    const resourceIds = w.cluster_ids ?? [];
    return {
      serviceType: KUBERNETES_WORKSPACE_TYPE,
      organizationId: normalizeId(w.organization_id),
      organizationName: w.name ?? '',
      home: false,
      // The Kubernetes console has no workspace in its URLs: the backend picks the tenant from the token.
      ref: null,
      scope: w.org_wide ? 'org' : projectIds.length > 0 ? 'projects' : 'resources',
      projectIds,
      resourceIds,
    };
  });
}

export function createKubernetesWorkspaceService(client: WorkspaceClient) {
  return defineWorkspaceService<HomeWorkspace | null, SharedItem[]>({
    type: KUBERNETES_WORKSPACE_TYPE,
    // Same key and answer as `useWorkspace`, the console's gate: one fetch serves both.
    home: {
      queryKey: K8S_WORKSPACE_KEY,
      staleTime: 60_000,
      fetch: async () => readK8sWorkspace<HomeWorkspace>((await client.get('/workspace')).data),
    },
    shared: {
      queryKey: SHARED_KEY,
      staleTime: 5 * 60_000,
      fetch: async () => {
        const items = field<SharedItem[]>((await client.get('/workspaces/shared')).data, 'workspaces');
        return Array.isArray(items) ? items : [];
      },
    },
    // An organization with no Kubernetes tenant has no workspace (`workspace: null`) and is simply not listed.
    homeEntries: (ws) =>
      ws?.organization_id
        ? [
            {
              serviceType: KUBERNETES_WORKSPACE_TYPE,
              organizationId: normalizeId(ws.organization_id),
              organizationName: ws.name ?? '',
              home: true,
              ref: null,
              scope: 'org',
              projectIds: [],
              resourceIds: [],
            },
          ]
        : [],
    sharedEntries,
    // The workspace is not in the URL, so `href` ignores it; the cluster list is the console's base.
    href: (_ws, page) => (page && page !== 'clusters' ? `${K8S_V2_BASE}/${page}` : K8S_V2_BASE),
  });
}

// The client is looked up per call, not at import: this file is in the entry chunk (module.ts) and the registry is filled at boot.
export const workspaceService = createKubernetesWorkspaceService({ get: (url) => getHttpClient('k8sV2Api').get(url) });
